import { request, requestData } from '@/lib/api-client';
import type {
  ChannelOverrideState,
  Closure,
  OpeningWindow,
  OpenNow,
  OperatingStatus,
  Pharmacy,
  PharmacyChannel,
  PharmacyLicense,
  ServiceChannel,
  Uuid,
} from './types';

export function getPharmacy(pharmacyId: Uuid): Promise<Pharmacy> {
  return requestData<Pharmacy>(`/pharmacies/${pharmacyId}`);
}

/**
 * The operational switches this shop owns: OPEN/BUSY/CLOSED and whether it is
 * taking orders. Gated on `pharmacy:operations:write`, which pharmacy staff hold
 * for their own branch — identity, address and GSTIN stay platform-only, since
 * those are what the licence was verified against.
 *
 * The backend refuses OPEN or online orders without a verified, unexpired
 * licence, and answers 409 with a readable message when there is none.
 */
export function setOperatingStatus(
  pharmacyId: Uuid,
  body: {
    operatingStatus: OperatingStatus;
    acceptsOnlineOrders?: boolean;
    acceptsCod?: boolean;
    reason?: string;
  },
): Promise<Pharmacy> {
  return requestData<Pharmacy>(`/pharmacies/${pharmacyId}/operating-status`, {
    method: 'PATCH',
    body,
  });
}


/**
 * The weekly schedule. Read is open to any signed-in user; writing needs
 * `pharmacy:operations:write`, which this shop's staff hold for their own branch.
 */
export function listHours(pharmacyId: Uuid): Promise<OpeningWindow[]> {
  return requestData<OpeningWindow[]>(`/pharmacies/${pharmacyId}/hours`);
}

/**
 * Replaces the entire week in one call — the backend does not patch a day at a
 * time, so a half-applied save cannot leave the shop open on a day it is shut.
 */
export function replaceHours(pharmacyId: Uuid, hours: OpeningWindow[]): Promise<OpeningWindow[]> {
  return requestData<OpeningWindow[]>(`/pharmacies/${pharmacyId}/hours`, {
    method: 'PUT',
    body: {
      hours: hours.map(({ dayOfWeek, opensAt, closesAt }) => ({ dayOfWeek, opensAt, closesAt })),
    },
  });
}

export function getOpenNow(pharmacyId: Uuid): Promise<OpenNow> {
  return requestData<OpenNow>(`/pharmacies/${pharmacyId}/open-now`);
}

export function listClosures(pharmacyId: Uuid): Promise<Closure[]> {
  return requestData<Closure[]>(`/pharmacies/${pharmacyId}/closures`);
}

export function createClosure(
  pharmacyId: Uuid,
  body: { startsAt: string; endsAt: string; reason: string },
): Promise<Closure> {
  return requestData<Closure>(`/pharmacies/${pharmacyId}/closures`, { method: 'POST', body });
}

export function deleteClosure(pharmacyId: Uuid, closureId: Uuid): Promise<void> {
  // 204, so there is no envelope to unwrap — `requestData` would read `.data`
  // off `undefined` and throw before the caller ever saw a success.
  return request<void>(`/pharmacies/${pharmacyId}/closures/${closureId}`, { method: 'DELETE' });
}


// ── channels ────────────────────────────────────────────────────────────────

export function listChannels(pharmacyId: Uuid): Promise<PharmacyChannel[]> {
  return requestData<PharmacyChannel[]>(`/pharmacies/${pharmacyId}/channels`);
}

/** The master switch for one channel — the counter, or the marketplace. */
export function setChannelEnabled(
  pharmacyId: Uuid,
  channel: ServiceChannel,
  isEnabled: boolean,
): Promise<PharmacyChannel> {
  return requestData<PharmacyChannel>(`/pharmacies/${pharmacyId}/channels/${channel}`, {
    method: 'PATCH',
    body: { isEnabled },
  });
}

/**
 * A bounded override on the channel's STATUS, not on the schedule.
 *
 * OPEN keeps it live past its hours; CLOSED takes it down inside them. The
 * weekly schedule is untouched either way and resumes when the override lapses.
 * `minutes` is required and capped server-side at 12 hours.
 */
export function setChannelOverride(
  pharmacyId: Uuid,
  channel: ServiceChannel,
  body: { state: ChannelOverrideState; minutes: number; reason?: string },
): Promise<PharmacyChannel> {
  return requestData<PharmacyChannel>(`/pharmacies/${pharmacyId}/channels/${channel}/override`, {
    method: 'POST',
    body,
  });
}

export function clearChannelOverride(
  pharmacyId: Uuid,
  channel: ServiceChannel,
): Promise<void> {
  // 204 as well; see `deleteClosure`.
  return request<void>(`/pharmacies/${pharmacyId}/channels/${channel}/override`, {
    method: 'DELETE',
  });
}

/**
 * The shop's drug licences, for the printed bill. A retail bill carries the
 * licence numbers it is sold under; the caller prints only verified, unexpired
 * ones.
 */
export function listLicenses(pharmacyId: Uuid): Promise<PharmacyLicense[]> {
  return requestData<PharmacyLicense[]>(`/pharmacies/${pharmacyId}/licenses`);
}
